import {
  type FileInfo,
  type SupportAgent,
  TICKET_KINDS,
  TICKET_MESSAGE_KINDS,
  TICKET_STATUSES,
  TICKET_URGENCIES,
  type TicketDetail,
  type TicketKind,
  type TicketStatus,
  type TicketUrgency,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { FileLink } from '../../components/FileLink';
import { HistoryButton } from '../../components/History';
import { useCrumbs } from '../../layout/crumbs';
import { ApiError, api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { TICKET_TONE } from '../../lib/support';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { KbDraftPanel } from './KbDraftPanel';
import { uploadSupportFiles } from './SupportDialogs';

export function TicketAttachments({
  entityType,
  entityId,
  canAdd,
}: {
  entityType: string;
  entityId: string;
  canAdd: boolean;
}) {
  const qc = useQueryClient();
  const ref = useRef<HTMLInputElement>(null);
  const q = useQuery({
    queryKey: ['support', 'files', entityType, entityId],
    queryFn: () => api<FileInfo[]>('/files', { query: { entityType, entityId } }),
  });
  const add = useMutation({
    mutationFn: async (list: File[]) => uploadSupportFiles(entityType, entityId, list),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support', 'files', entityType, entityId] }),
  });
  return (
    <Panel title="Файлы и снимки экрана">
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data.length ? (
        <ul className="flex flex-col gap-1.5 text-[14px]">
          {q.data.map((f) => (
            <li key={f.id}>
              <FileLink file={f} />
              <span className="text-[12px] text-ink-2"> · {dateTime(f.createdAt)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">Файлов нет.</p>
      )}
      {canAdd ? (
        <div className="mt-3">
          <input
            ref={ref}
            type="file"
            multiple
            accept="image/*,.pdf,.txt,.xlsx,.docx"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) add.mutate(Array.from(e.target.files).slice(0, 5));
              if (ref.current) ref.current.value = '';
            }}
          />
          <Button loading={add.isPending} onClick={() => ref.current?.click()}>
            Приложить файл
          </Button>
        </div>
      ) : null}
    </Panel>
  );
}

/** Оценка ответа автором: решён ли вопрос и от 1 до 5 (БЗ-27) */
function RatingPanel({ t, onDone }: { t: TicketDetail; onDone: () => void }) {
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState('');
  const send = useMutation({
    mutationFn: (solved: boolean) =>
      api<TicketDetail>(`/support/tickets/${t.id}/rating`, {
        method: 'POST',
        body: { solved, score, ...(comment.trim() ? { comment: comment.trim() } : {}) },
      }),
    onSuccess: () => {
      toast('Спасибо за оценку');
      onDone();
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Не удалось сохранить оценку', 'danger'),
  });
  if (t.rating)
    return (
      <Panel title="Оценка ответа">
        <p className="text-[14px]">
          {t.rating.score} из 5 · {t.rating.solved ? 'вопрос решён' : 'вопрос не решён'}
        </p>
        {t.rating.comment ? (
          <p className="mt-1 text-[14px] text-ink-2 [overflow-wrap:anywhere]">{t.rating.comment}</p>
        ) : null}
      </Panel>
    );
  if (!t.canRate) return null;
  return (
    <Panel title="Оцените ответ">
      <div className="flex flex-col gap-3">
        <fieldset className="m-0 flex min-w-0 flex-wrap gap-2 border-0 p-0">
          <legend className="sr-only">Оценка от 1 до 5</legend>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={score === n}
              onClick={() => setScore(n)}
              className={`min-h-11 min-w-11 rounded-md border px-3 text-[15px] font-semibold ${
                score === n ? 'border-accent bg-accent text-white' : 'border-line bg-surface text-ink'
              }`}
            >
              {n}
            </button>
          ))}
        </fieldset>
        <Field label="Что можно улучшить (необязательно)" htmlFor="tk-rate-comment">
          <Textarea
            id="tk-rate-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={1000}
            rows={2}
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            disabled={!score}
            loading={send.isPending}
            onClick={() => send.mutate(true)}
          >
            Вопрос решён
          </Button>
          <Button disabled={!score} loading={send.isPending} onClick={() => send.mutate(false)}>
            Не решён
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** Передача ошибки разработке, выпуск, с которым обращение закроется само, и отметка выпуска вручную (БЗ-25) */
function DevPanel({ t, onDone }: { t: TicketDetail; onDone: () => void }) {
  const [steps, setSteps] = useState('');
  const [note, setNote] = useState('');
  const [releaseId, setReleaseId] = useState('');
  const [linkNote, setLinkNote] = useState('');
  const [manual, setManual] = useState(false);
  const fail = (e: unknown) => toast(e instanceof ApiError ? e.message : 'Не удалось сохранить', 'danger');
  const handoff = useMutation({
    mutationFn: () =>
      api<TicketDetail>(`/support/tickets/${t.id}/handoff`, { method: 'POST', body: { steps } }),
    onSuccess: () => {
      toast('Передано разработке');
      setSteps('');
      onDone();
    },
    onError: fail,
  });
  const release = useMutation({
    mutationFn: () =>
      api<TicketDetail>(`/support/tickets/${t.id}/release`, { method: 'POST', body: { note } }),
    onSuccess: () => {
      toast('Выпуск отмечен, автор уведомлён');
      setNote('');
      onDone();
    },
    onError: fail,
  });
  const link = useMutation({
    mutationFn: () =>
      api<TicketDetail>(`/support/tickets/${t.id}/release-link`, {
        method: 'POST',
        body: { releaseId: releaseId.trim(), note: linkNote },
      }),
    onSuccess: (d) => {
      toast(d.dev?.releasedAt ? 'Выпуск уже выложен: обращение закрыто, автор уведомлён' : 'Выпуск указан');
      setReleaseId('');
      setLinkNote('');
      onDone();
    },
    onError: fail,
  });
  if (!t.dev && !t.canHandoff) return null;
  const idOk = /^[a-z0-9][a-z0-9-]{2,99}$/.test(releaseId.trim());
  return (
    <Panel title="Разработка">
      {t.dev ? (
        <div className="flex flex-col gap-2 text-[14px]">
          <p className="text-ink-2">
            Передано {dateTime(t.dev.handedAt)}
            {t.dev.handedByName ? `, ${t.dev.handedByName}` : ''}
          </p>
          <p className="leading-6 [overflow-wrap:anywhere] whitespace-pre-wrap">{t.dev.steps}</p>
          {t.dev.releasedAt ? (
            <p className="text-ink [overflow-wrap:anywhere]">
              Исправлено {dateTime(t.dev.releasedAt)}: {t.dev.releaseNote}
              {t.dev.releaseTitle ? ` (обновление «${t.dev.releaseTitle}»)` : ''}
            </p>
          ) : t.dev.releaseId ? (
            <p className="text-ink [overflow-wrap:anywhere]">
              Исправление войдёт в выпуск <span className="font-mono text-[13px]">{t.dev.releaseId}</span>
              {t.dev.releaseNote ? `: ${t.dev.releaseNote}` : ''}. Обращение закроется само в день выкладки,
              автор получит сообщение.
            </p>
          ) : null}
        </div>
      ) : null}
      {t.canHandoff ? (
        <div className="flex flex-col gap-3">
          <p className="text-[13px] text-ink-2">
            Ошибку, которую нельзя исправить настройкой, передайте разработке. Вид обращения должен быть
            «Ошибка».
          </p>
          <Field label="Шаги воспроизведения" htmlFor="tk-steps">
            <Textarea
              id="tk-steps"
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              maxLength={4000}
              rows={5}
              placeholder="1. Открыть… 2. Нажать… Ожидали… Получили…"
            />
          </Field>
          <div>
            <Button
              loading={handoff.isPending}
              disabled={steps.trim().length < 20}
              onClick={() => handoff.mutate()}
            >
              Передать разработке
            </Button>
          </div>
        </div>
      ) : null}
      {t.canLinkRelease ? (
        <div className="mt-3 flex flex-col gap-3">
          <p className="text-[13px] text-ink-2">
            Укажите выпуск — идентификатор записи «Что нового», с которой выйдет исправление. В день выкладки
            обращение закроется само, автор получит сообщение.
          </p>
          <Field label="Идентификатор выпуска" htmlFor="tk-release-id">
            <Input
              id="tk-release-id"
              className="h-11 font-mono md:h-10"
              value={releaseId}
              onChange={(e) => setReleaseId(e.target.value)}
              maxLength={100}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder={t.dev?.releaseId ?? '2026-11-02-site-photo-fix'}
            />
          </Field>
          <Field label="Что исправлено простыми словами" htmlFor="tk-link-note">
            <Textarea
              id="tk-link-note"
              value={linkNote}
              onChange={(e) => setLinkNote(e.target.value)}
              maxLength={1000}
              rows={3}
            />
          </Field>
          <div>
            <Button
              loading={link.isPending}
              disabled={!idOk || linkNote.trim().length < 5}
              onClick={() => link.mutate()}
            >
              {t.dev?.releaseId ? 'Сменить выпуск' : 'Закрыть при выкладке'}
            </Button>
          </div>
        </div>
      ) : null}
      {t.canRelease && !manual ? (
        <div className="mt-3">
          <Button variant="ghost" onClick={() => setManual(true)}>
            Отметить выпуск вручную
          </Button>
        </div>
      ) : null}
      {t.canRelease && manual ? (
        <div className="mt-3 flex flex-col gap-3">
          <Field label="Что исправлено (попадёт в «Что нового»)" htmlFor="tk-note">
            <Textarea
              id="tk-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              rows={3}
            />
          </Field>
          <div>
            <Button
              variant="primary"
              loading={release.isPending}
              disabled={note.trim().length < 5}
              onClick={() => release.mutate()}
            >
              Исправление выпущено
            </Button>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

/** Обращение: переписка, срок первого ответа, статус, исполнитель; специалист поддержки меняет статус и исполнителя (БЗ-23, БЗ-24) */
export function TicketPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['support', 'ticket', id],
    queryFn: () => api<TicketDetail>(`/support/tickets/${id}`),
  });
  const t = q.data;
  useCrumbs(t ? [{ label: t.number, title: t.subject }] : null, q.error);
  const [text, setText] = useState('');
  const [after, setAfter] = useState<TicketStatus | ''>('');
  const [err, setErr] = useState<string | null>(null);
  const agents = useQuery({
    queryKey: ['support', 'agents'],
    queryFn: () => api<SupportAgent[]>('/support/agents'),
    enabled: !!t?.canManage,
  });
  // Автор открыл обращение: ответы прочитаны (снимает отметку «Есть ответ» и пункт в «Ждёт меня»)
  useEffect(() => {
    if (t?.unread)
      api(`/support/tickets/${id}/seen`, { method: 'POST' })
        .then(() => qc.invalidateQueries({ queryKey: ['support'] }))
        .catch(() => {});
  }, [t?.unread, id, qc]);
  const refresh = () => qc.invalidateQueries({ queryKey: ['support'] });
  const reply = useMutation({
    mutationFn: () =>
      api<TicketDetail>(`/support/tickets/${id}/messages`, {
        method: 'POST',
        body: { body: text, ...(after ? { status: after } : {}) },
      }),
    onSuccess: () => {
      setText('');
      setAfter('');
      setErr(null);
      refresh();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Не удалось отправить'),
  });
  const patch = useMutation({
    mutationFn: (
      body: Partial<{
        status: TicketStatus;
        assigneeId: string | null;
        urgency: TicketUrgency;
        kind: TicketKind;
      }>,
    ) => api<TicketDetail>(`/support/tickets/${id}`, { method: 'PATCH', body }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Не удалось изменить', 'danger'),
  });
  const close = useMutation({
    mutationFn: () => api<TicketDetail>(`/support/tickets/${id}/close`, { method: 'POST' }),
    onSuccess: () => {
      toast('Обращение закрыто');
      refresh();
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Не удалось закрыть', 'danger'),
  });

  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError || !t)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );

  const due = t.firstResponseAt
    ? `ответили ${dateTime(t.firstResponseAt)}`
    : t.firstResponseDue
      ? `${t.overdue ? 'срок прошёл' : 'ответим до'} ${dateTime(t.firstResponseDue)}`
      : '—';
  return (
    <Page>
      <PageHeader
        title={`${t.number}. ${t.subject}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={TICKET_TONE[t.status]}>{TICKET_STATUSES[t.status]}</Badge>
            {t.urgency === 'blocking' ? <Badge tone="danger">{TICKET_URGENCIES.blocking}</Badge> : null}
            {t.overdue ? <Badge tone="danger">Ответ просрочен</Badge> : null}
            {t.escalated ? (
              <Badge tone="warning">
                Передано руководителю{t.escalatedToName ? `: ${t.escalatedToName}` : ''}
              </Badge>
            ) : null}
            {t.resolvedByPilot ? <Badge tone="neutral">Решено Pilot</Badge> : null}
          </span>
        }
        actions={
          <>
            {t.canClose ? (
              <Button loading={close.isPending} onClick={() => close.mutate()}>
                Закрыть обращение
              </Button>
            ) : null}
            {t.canManage ? <HistoryButton table="tickets" rowId={t.id} title={t.number} /> : null}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <Panel title="Обращение">
            <p className="text-[14px] leading-6 [overflow-wrap:anywhere] whitespace-pre-wrap">{t.body}</p>
          </Panel>
          <Panel title="Переписка">
            <ul className="flex flex-col gap-3">
              {t.messages.length === 0 ? <li className="text-[14px] text-ink-2">Ответа пока нет.</li> : null}
              {t.messages.map((m) => (
                <li
                  key={m.id}
                  className={
                    m.kind === 'system'
                      ? 'text-[13px] text-ink-2'
                      : m.kind === 'support' || m.kind === 'pilot'
                        ? 'rounded-md border border-line bg-surface-2 p-3'
                        : 'rounded-md border border-line p-3'
                  }
                >
                  {m.kind === 'system' ? null : (
                    <div className="mb-1 text-[12px] text-ink-2">
                      {m.authorName ?? TICKET_MESSAGE_KINDS[m.kind]} · {TICKET_MESSAGE_KINDS[m.kind]} ·{' '}
                      {dateTime(m.createdAt)}
                    </div>
                  )}
                  <p className="text-[14px] leading-6 [overflow-wrap:anywhere] whitespace-pre-wrap">
                    {m.kind === 'system' ? `${dateTime(m.createdAt)} · ${m.body}` : m.body}
                  </p>
                </li>
              ))}
            </ul>
            {t.canReply ? (
              <div className="mt-4 flex flex-col gap-3">
                <Field label={t.canManage ? 'Ответ автору' : 'Дополнить обращение'} htmlFor="tk-reply">
                  <Textarea
                    id="tk-reply"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={4000}
                    rows={4}
                  />
                </Field>
                {t.canManage ? (
                  <Field label="Статус после ответа" htmlFor="tk-after">
                    <Select
                      className="h-11 md:h-10"
                      id="tk-after"
                      value={after}
                      onChange={(e) => setAfter(e.target.value as TicketStatus | '')}
                    >
                      <option value="">Не менять</option>
                      {Object.entries(TICKET_STATUSES).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}
                {err ? <ErrorBox error={new Error(err)} /> : null}
                <div>
                  <Button
                    variant="primary"
                    loading={reply.isPending}
                    disabled={!text.trim()}
                    onClick={() => reply.mutate()}
                  >
                    Отправить
                  </Button>
                </div>
              </div>
            ) : null}
          </Panel>
          <RatingPanel t={t} onDone={refresh} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <Panel title="Сведения">
            <KeyValue
              items={[
                { label: 'Автор', value: t.authorName },
                { label: 'Вид', value: TICKET_KINDS[t.kind] },
                { label: 'Ответственный', value: t.assigneeName ?? 'не назначен' },
                { label: 'Первый ответ', value: due },
                { label: 'Создано', value: dateTime(t.createdAt) },
                ...(t.canManage
                  ? [
                      {
                        label: 'Страница',
                        value: t.pagePath ? `${t.pageTitle ?? ''} ${t.pagePath}`.trim() : '—',
                      },
                      { label: 'Роль автора', value: t.roleNames ?? '—' },
                      {
                        label: 'Устройство',
                        value: <span className="[overflow-wrap:anywhere]">{t.device ?? '—'}</span>,
                      },
                      { label: 'Версия портала', value: t.appVersion ?? '—' },
                    ]
                  : []),
              ]}
            />
          </Panel>
          {t.canManage ? (
            <Panel title="Управление">
              <div className="flex flex-col gap-3">
                <Field label="Статус" htmlFor="tk-status">
                  <Select
                    className="h-11 md:h-10"
                    id="tk-status"
                    value={t.status}
                    onChange={(e) => patch.mutate({ status: e.target.value as TicketStatus })}
                  >
                    {Object.entries(TICKET_STATUSES).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Ответственный" htmlFor="tk-assignee">
                  <Select
                    className="h-11 md:h-10"
                    id="tk-assignee"
                    value={t.assigneeId ?? ''}
                    onChange={(e) => patch.mutate({ assigneeId: e.target.value || null })}
                  >
                    <option value="">Не назначен</option>
                    {(agents.data ?? []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.fullName} (открыто: {a.openTickets})
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Срочность" htmlFor="tk-urgency">
                  <Select
                    className="h-11 md:h-10"
                    id="tk-urgency"
                    value={t.urgency}
                    onChange={(e) => patch.mutate({ urgency: e.target.value as TicketUrgency })}
                  >
                    {Object.entries(TICKET_URGENCIES).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Вид" htmlFor="tk-kind">
                  <Select
                    className="h-11 md:h-10"
                    id="tk-kind"
                    value={t.kind}
                    onChange={(e) => patch.mutate({ kind: e.target.value as TicketKind })}
                  >
                    {Object.entries(TICKET_KINDS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            </Panel>
          ) : null}
          <DevPanel t={t} onDone={refresh} />
          <KbDraftPanel t={t} onDone={refresh} />
          <TicketAttachments entityType="support_ticket" entityId={t.id} canAdd={t.canReply} />
        </div>
      </div>
    </Page>
  );
}
